import React, { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { useStore } from '../../store';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@roger/ui-components';
import { client, getWoundCareWorksheetPdf } from '../../services/api';

const WOUND_CARE_DISCLAIMER =
  'All wound assessments documented for this visit have been reviewed for accuracy and reflect the clinical findings at the time of the visit.';

const DIGITAL_SIGNATURE_CONFIRMATION =
  'I confirm that I am digitally signing this wound care worksheet';

interface WoundCareWorksheetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  visitId: string;
  onDocumentCreated?: () => void;
}

const WoundCareWorksheetModal: React.FC<WoundCareWorksheetModalProps> = ({
  open,
  onOpenChange,
  patientId,
  visitId,
  onDocumentCreated,
}) => {
  const currentUser = useStore(state => state.user);
  const signerDisplay = currentUser?.name || currentUser?.email || 'Unknown';

  const [confirmed, setConfirmed] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const response = await getWoundCareWorksheetPdf({
        path: { patient_id: patientId },
        body: { visit_id: visitId },
        client,
      });

      const blob = response.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wound_care_worksheet.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      handleClose();
      onDocumentCreated?.();
    } catch (err) {
      console.error('Failed to generate wound care worksheet:', err);
      setError('Failed to generate wound care worksheet. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      onOpenChange(false);
      setConfirmed(false);
      setError(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>Export Wound Care Worksheet</DialogTitle>
          <DialogDescription>
            Generate a signed PDF report of wound assessments for this visit.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          <div className='border-t pt-4 space-y-3'>
            <p className='text-sm text-muted-foreground'>
              {WOUND_CARE_DISCLAIMER}
            </p>
            <div className='flex items-start space-x-2'>
              <Checkbox
                id='wound-digital-signature'
                checked={confirmed}
                onCheckedChange={checked => setConfirmed(checked === true)}
                className='mt-0.5'
              />
              <div className='space-y-1'>
                <Label
                  htmlFor='wound-digital-signature'
                  className='text-sm font-medium cursor-pointer leading-tight'
                >
                  {DIGITAL_SIGNATURE_CONFIRMATION}
                </Label>
                <p className='text-xs text-muted-foreground'>
                  Signed by: <strong>{signerDisplay}</strong>
                </p>
              </div>
            </div>
          </div>

          {error && <p className='text-sm text-destructive'>{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!confirmed || isGenerating}
          >
            {isGenerating && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            <FileText className='h-4 w-4 mr-2' />
            Generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WoundCareWorksheetModal;
